<section class="pp" [class.pp-open]="open()">
  <header class="pp-head" (click)="toggle()">
    <div class="pp-title">
      <svg class="pp-chev" [class.open]="open()" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="9 18 15 12 9 6" />
      </svg>
      <span class="pp-label">Pages</span>
      <span class="pp-count">{{ totalCount() }}</span>
    </div>
    <div class="pp-head-right">
      <div class="pp-stats">
        <span class="pp-stat review"><span class="pp-stat-dot"></span>{{ reviewCount() }} Review</span>
        <span class="pp-stat revise"><span class="pp-stat-dot"></span>{{ reviseCount() }} Revise</span>
        <span class="pp-stat approved"><span class="pp-stat-dot"></span>{{ approvedCount() }} Approved</span>
      </div>

      <input #csvInput type="file" accept=".csv,text/csv" hidden (change)="onCsvSelected($event)" />
      <button class="pp-import" type="button" (click)="$event.stopPropagation(); csvInput.click()" [disabled]="busy()">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
          stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Import CSV
      </button>

      <button class="pp-add" type="button" (click)="startAdding($event)">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
          stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        Add Page
      </button>
    </div>
  </header>

  @if (open()) {
    <!-- Opens over the content below rather than pushing it down. -->
    <div class="pp-body">
      <div class="pp-filters">
        <input
          class="pp-search"
          type="search"
          placeholder="Search page URLs"
          aria-label="Search page URLs"
          [value]="searchText()"
          (input)="searchText.set($any($event.target).value)"
        />
        <select
          class="pp-status-filter"
          aria-label="Filter by status"
          [value]="statusFilter()"
          (change)="onStatusFilterChange($any($event.target).value)"
        >
          <option value="">All statuses</option>
          @for (statusOption of statusFilterOptions; track statusOption.value) {
            <option [value]="statusOption.value">{{ statusOption.label }}</option>
          }
        </select>
        @if (loading()) {
          <span class="pp-loading">Loading…</span>
        }
      </div>

      <!-- Pagination above the list too, so a long page doesn't need scrolling to move on. -->
      <fb-pagination
        class="pp-pagination-top"
        [page]="currentPage()"
        [total]="filteredTotal()"
        [perPage]="perPage"
        label="pages"
        (pageChange)="onPageChange($event)"
      />

      <div class="pp-table" [class.pp-table-loading]="loading()">
        <div class="pp-row pp-th">
          <span>Page URL</span>
          <span>Assigned Mockup</span>
          <span>Status</span>
        </div>

        <!-- add-page row -->
        @if (adding()) {
          <div class="pp-row pp-add-row">
            <input
              class="pp-url-input"
              type="text"
              placeholder="https://example.com/page"
              [value]="draftPageUrl()"
              (input)="draftPageUrl.set($any($event.target).value)"
            />

            <!-- mockup dropdown (draft) -->
            <div class="pp-mockup">
              <button class="pp-mk-trigger" type="button" (click)="toggleMockupMenu(addRowKey)" [class.open]="openMockupFor() === addRowKey">
                @if (mockupLabel(draftMockup()); as label) {
                  <img class="pp-mk-thumb" [src]="draftMockup()" [alt]="label" />
                  <span class="pp-mk-label">{{ label }}</span>
                } @else {
                  <span class="pp-mk-label muted">Assign mockup</span>
                }
                <svg class="pp-mk-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                  stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
              </button>
              @if (openMockupFor() === addRowKey) {
                <div class="pp-mk-menu" role="listbox">
                  @for (option of mockupOptions(); track option.value) {
                    <button class="pp-mk-item" type="button" (click)="selectDraftMockup(option.value)" [title]="option.label">
                      <img class="pp-mk-thumb" [src]="option.thumbnail" [alt]="option.label" loading="lazy" />
                      <span class="pp-mk-label">{{ option.label }}</span>
                      @if (option.value === draftMockup()) { <span class="pp-mk-check">✓</span> }
                    </button>
                  } @empty {
                    <div class="pp-mk-empty">No mockups uploaded yet</div>
                  }
                </div>
              }
            </div>

            <div class="pp-add-actions">
              <button class="pp-btn primary" type="button" (click)="addPage()" [disabled]="!canAddPage()">Add Page</button>
              <button class="pp-btn ghost" type="button" (click)="cancelAdding()" [disabled]="busy()">Cancel</button>
            </div>
          </div>
        }

        <!-- page rows -->
        @for (page of pages(); track page.fullUrl ?? page.pageUrl) {
          <div class="pp-row">
            <!-- The icon and the URL open the page in a new window (web URLs only). -->
            <a
              class="pp-url"
              [attr.href]="isWebUrl(pageTarget(page)) ? pageReviewLink(pageTarget(page)) : null"
              target="_blank"
              rel="noopener noreferrer"
              [title]="'Open ' + pageTarget(page) + ' in a new window'"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              <span class="pp-url-stack">
                <span class="pp-url-text">{{ page.pageUrl }}</span>
                @if (page.fullUrl && page.fullUrl !== page.pageUrl) {
                  <span class="pp-url-full">{{ page.fullUrl }}</span>
                }
              </span>
            </a>

            <!-- mockup dropdown (row) -->
            <div class="pp-mockup">
              <button class="pp-mk-trigger" type="button" (click)="toggleMockupMenu(pageTarget(page))" [class.open]="openMockupFor() === pageTarget(page)" [disabled]="busy()">
                @if (mockupLabel(page.mockupUrl); as label) {
                  <img class="pp-mk-thumb" [src]="page.mockupUrl" [alt]="label" />
                  <span class="pp-mk-label">{{ label }}</span>
                } @else {
                  <span class="pp-mk-label muted">Assign mockup</span>
                }
                <svg class="pp-mk-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                  stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
              </button>
              @if (openMockupFor() === pageTarget(page)) {
                <div class="pp-mk-menu" role="listbox">
                  @for (option of mockupOptions(); track option.value) {
                    <button class="pp-mk-item" type="button" (click)="selectRowMockup(page, option.value)" [title]="option.label">
                      <img class="pp-mk-thumb" [src]="option.thumbnail" [alt]="option.label" loading="lazy" />
                      <span class="pp-mk-label">{{ option.label }}</span>
                      @if (option.value === page.mockupUrl) { <span class="pp-mk-check">✓</span> }
                    </button>
                  } @empty {
                    <div class="pp-mk-empty">No mockups uploaded yet</div>
                  }
                </div>
              }
            </div>

            <fb-status-chip
              [statuses]="statuses()"
              [current]="page.status"
              (statusChange)="onStatusChange(page, $event)"
            />
          </div>
        } @empty {
          @if (!adding() && !loading()) {
            @if (isFiltered()) {
              <div class="pp-pages-empty">
                <div class="pp-pages-empty-title">No matching pages</div>
                <div class="pp-pages-empty-sub">Try a different search or status.</div>
              </div>
            } @else {
              <div class="pp-pages-empty">
                <div class="pp-pages-empty-title">No pages yet</div>
                <div class="pp-pages-empty-sub">Add a page and assign a mockup to get started.</div>
              </div>
            }
          }
        }
      </div>

      <fb-pagination
        [page]="currentPage()"
        [total]="filteredTotal()"
        [perPage]="perPage"
        label="pages"
        (pageChange)="onPageChange($event)"
      />
    </div>
  }

  @if (openMockupFor() !== null) {
    <div class="pp-backdrop" (click)="openMockupFor.set(null)"></div>
  }
</section>
